import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  useTheme,
  alpha,
  CircularProgress,
  Fade,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import UpdateIcon from '@mui/icons-material/Update';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoLight from '../../assets/Logo OK.png';
import LogoDark from '../../assets/image.png';
import api from '../../services/api';

const Login = ({ toggleColorMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userInfo', JSON.stringify(response.data.user));
      navigate('/history');
    } catch (error) {
      console.error('Erro:', error);
      setError(error.response?.data?.detail || 'Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <SettingsIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Análise Inteligente',
      description: 'Sistema inteligente que analisa e determina a competência territorial de forma precisa e eficiente.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Segurança Garantida',
      description: 'Decisões baseadas na legislação vigente, garantindo segurança jurídica para todos os envolvidos.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Resposta Rápida',
      description: 'Obtenha respostas instantâneas sobre a competência territorial para cada caso específico.'
    },
    {
      icon: <UpdateIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Sempre Atualizado',
      description: 'Sistema constantemente atualizado com as mais recentes normativas e legislações.'
    },
    {
      icon: <AutoGraphIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Interface Intuitiva',
      description: 'Navegação simples e objetiva, facilitando a consulta e tomada de decisões.'
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        background: theme.palette.mode === 'dark' 
          ? '#1E1E1E'
          : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 4 }
      }}
    >
      {/* Botão de alternar tema */}
      <Tooltip title={`Mudar para modo ${theme.palette.mode === 'dark' ? 'claro' : 'escuro'}`}>
        <IconButton
          onClick={toggleColorMode}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: alpha(theme.palette.background.paper, 0.2),
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, 0.3),
            }
          }}
        >
          {theme.palette.mode === 'dark' ? (
            <Brightness7Icon sx={{ color: '#fff' }} />
          ) : (
            <Brightness4Icon sx={{ color: '#000' }} />
          )}
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          width: '100%',
          maxWidth: '1000px',
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 2,
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Lado Esquerdo - Features */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 4,
            mt: 4
          }}
        >
          <Stack spacing={2}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.6)
                    : alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.7)
                      : alpha(theme.palette.background.paper, 0.9),
                    transform: 'translateX(8px)'
                  }
                }}
              >
                {feature.icon}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      color: theme.palette.mode === 'dark' ? '#fff' : '#1a1f2c'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.7)'
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Lado Direito - Login */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 2, md: 6 }
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 3,
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 30px rgba(0, 0, 0, 0.3)'
                : '0 4px 30px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4
              }}
            >
              <Box
                component="img"
                src={theme.palette.mode === 'dark' ? LogoDark : LogoLight}
                alt="Logo"
                sx={{
                  width: '200px',
                  height: 'auto',
                  mb: 3
                }}
              />
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.error.main, 0.1)
                    : alpha(theme.palette.error.light, 0.1),
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.error.light
                    : theme.palette.error.dark,
                  '& .MuiAlert-icon': {
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.error.light
                      : theme.palette.error.dark
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.7)'
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.1)
                          : alpha(theme.palette.background.default, 0.04),
                        '& fieldset': {
                          borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : 'rgba(0, 0, 0, 0.15)'
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.25)'
                            : 'rgba(0, 0, 0, 0.25)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.7)'
                      },
                      '& .MuiInputBase-input': {
                        color: theme.palette.mode === 'dark'
                          ? '#fff'
                          : '#000'
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.7)'
                    }}
                  >
                    Senha
                  </Typography>
                  <TextField
                    fullWidth
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.1)
                          : alpha(theme.palette.background.default, 0.04),
                        '& fieldset': {
                          borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : 'rgba(0, 0, 0, 0.15)'
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.25)'
                            : 'rgba(0, 0, 0, 0.25)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.7)'
                      },
                      '& .MuiInputBase-input': {
                        color: theme.palette.mode === 'dark'
                          ? '#fff'
                          : '#000'
                      }
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        }
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.7)'
                      }}
                    >
                      Lembrar-me
                    </Typography>
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(45deg, #90CAF9 30%, #64B5F6 90%)'
                      : 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 150, 243, 0.3)',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(45deg, #64B5F6 30%, #42A5F5 90%)'
                        : 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(33, 150, 243, 0.3)'
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Login; 